/**
 * 261030 Phase B / B1 — bước ② Tài sản của khung 4 bước. CHỈ logic thuần (không React / antd / router) để jest chạy được.
 * Hình dạng dữ liệu = hợp đồng `b1-fe-contract.md` Rev 2 (BE `admin-service/src/utils/filmWorkflowFourAssets.ts`).
 */
import type { WorkflowFourStatus } from './workflowFourUtils';
import { failCodeOffersAbandon, failCodeOnlyAbandon } from './workflowFourUtils';
import type { AssetCharacterGroup } from './workflowFourAssetGroupsUtils';

export type GateVerdict = 'NOT_APPROVED' | 'OK' | 'OUTDATED' | 'REVOKED';
export type AssetsBlockCode =
  | 'STEP1_NOT_APPROVED' | 'ASSETS_NOT_RUN' | 'ASSETS_RUNNING' | 'ASSETS_MISSING_IMAGES' | 'ASSETS_FAILED' | 'KYC_BLOCKING' | 'ASSETS_VIEWS_MISSING' | 'UNCERTAIN_OPEN' | 'ASSETS_TEXT_NOT_READY';
export type AssetCardStatus = 'READY' | 'MISSING' | 'STALE' | 'RUNNING' | 'FAILED' | 'BLOCKED' | 'UNCERTAIN' | 'WAITING_BASE';
export type AssetKycStatus = 'NOT_REQUIRED' | 'MISSING' | 'PROCESSING' | 'ACTIVE' | 'FAILED' | 'UNKNOWN';
export type AssetTabKey = 'characters' | 'animals' | 'crowds' | 'props' | 'locations';

export interface AssetsBlockReason { code: AssetsBlockCode | string; message: string; keys?: string[] }
export interface AssetsEstimate {
  stagingRefresh?: { scenes:number; usd:number };
  images: { look: number; animal: number; crowd: number; prop: number; location: number; view: number; total: number };
  /** B1-fix2: lời gọi CHỮ trả phí của đoạn 7 (gom hướng bối cảnh), đã nằm trong `usd`. Vắng ⇒ 0. */
  calls?: { directions: number; usd: number };
  /** B3-3: ảnh NGƯỜI có tuổi nguồn sẽ được tạo ⇒ máy kiểm tuổi bằng vision (chỉ hiển thị — KHÔNG nằm trong `usd` và `hash`). */
  ageCheck?: { images: number; unitUsd: number; usd: number };
  usd: number;
  unit?: { look: number; animal: number; crowd: number; prop: number; location: number; view: number };
  source?: 'MEASURED_REFERENCE';
  hash: string;
  keys?: string[];
}
export interface AssetCard {
  key: string;
  ownerType: 'look' | 'crowd' | 'prop';
  ownerId: string | null;
  characterId: string | null;
  kind: 'PERSON' | 'ANIMAL' | 'CROWD' | 'PROP';
  name: string;
  lookKey: string | null;
  isMain: boolean;
  variantOf: { characterId: string; name: string; kind: 'AGE' | 'FORM'; description: string | null } | null;
  ageOrForm: string | null;
  species: string | null;
  description: string | null;
  imageUrl: string | null;
  status: AssetCardStatus;
  blocker: { code: string; message: string } | null;
  error: { code: string; message: string; retryable: boolean } | null;
  kyc: { required: boolean; status: AssetKycStatus; rowId: string | null; code: string | null; message: string | null };
  source: { kind: 'AI' | 'UPLOAD' | 'CUSTOM_PROMPT'; at: string; by: string | null } | null;
  protectedByHuman: boolean;
  eventIds: string[];
  sourceOnly: boolean;
}
export interface AssetLocationView { key: string; directionKey: string; label: string | null; imageUrl: string | null; status: AssetCardStatus; approved: boolean; error: { code: string; message: string } | null }
export interface AssetLocationCard {
  key: string; ownerId: string; name: string; imageUrl: string | null; status: AssetCardStatus;
  blocker: { code: string; message: string } | null; error: { code: string; message: string; retryable?: boolean } | null;
  approved: boolean; views: AssetLocationView[]; expectedViewKeys: string[];
  /** B1-fix1: ảnh chuẩn bối cảnh do người tải / sửa bằng prompt tay cũng được BE bảo vệ (máy không gen đè). */
  source?: AssetCard['source']; protectedByHuman?: boolean;
  /** Phase S #5: AI gom hướng quay bị cụt JSON / lỗi ⇒ máy dùng 2 hướng mặc định (trong/ngoài). Thông tin, không chặn duyệt. */
  directionFallback?: 'DEFAULT_AFTER_TRUNCATION' | 'DEFAULT_AFTER_ERROR';
  /** S12c T1: góc ảnh hướng bị bộ lọc an toàn của nhà cung cấp chặn dù máy đã thử mô tả dịu + model khác ⇒ máy BỎ QUA (ảnh chính vẫn dùng); `message` là câu Việt của BE. Vắng ⇒ không góc nào bị bỏ. */
  skippedViews?: Array<{ viewId: string; directionKey: string; label: string; message: string }>;
}
export interface AssetsSkipped { kind: 'PROP' | 'PERSON' | 'ANIMAL' | 'CROWD' | 'LOCATION'; ownerId: string | null; name: string; reasonCode: string; reason: string }
export type AssetsMode = 'READY_TO_START' | 'RUNNING' | 'STOPPED' | 'ERROR' | 'HAS_RESULT' | 'APPROVED' | 'LOCKED';
export interface AssetsStatus {
  version: 1;
  mode: AssetsMode;
  progress: { state: string; done: number; total: number; remaining: number; label: string };
  /** B3-5: một nhân vật một thẻ (gồm con vật); mỗi hàng là hình thái × bộ đồ, `looks` = `key` thẻ ở `tabs.*`. Vắng ⇒ FE gom từ `tabs`. */
  characterGroups?: AssetCharacterGroup[];
  tabs: { characters: AssetCard[]; animals: AssetCard[]; crowds: AssetCard[]; props: AssetCard[]; locations: AssetLocationCard[] };
  skipped: AssetsSkipped[];
  counts: { total: number; ready: number; missing: number; stale: number; running: number; failed: number; blocked: number; uncertain: number; kycPending: number; kycFailed: number };
  estimate: AssetsEstimate;
  gate: { hash: string | null; verdict: GateVerdict; canApprove: boolean; canApproveWhy: AssetsBlockReason | null };
  protectedCount: number;
  lastRun: { runId: string; state: string; endedAt: string | null; failedKeys: string[] } | null;
}
export interface AssetsImpactSubject {
  subjectKey: string; name: string | null;
  events: Array<{ eventId: string; title: string | null; hasStoryboard: boolean; hasMap: boolean; hasClip: boolean }>;
  storyboardEventIds: string[]; mapIds: string[];
  approvalImpact: { assetsGate: 'WILL_OUTDATE' | 'NONE' };   // S12h: BE vẫn dùng khoá này; nghĩa mới = «sửa xong sẽ có nút Làm lại từ bước ②» (cổng KHÔNG bị hạ)
}
export interface AssetsImpact { subjects: AssetsImpactSubject[] }
export interface AssetsRunResponse { started: boolean; segment?: number; chainId?: string; taskId?: string | null; reason?: string }
export interface AssetsRunBody { requestId: string; confirm: true; estimateHash: string; scope?: 'missing' | 'failed'; baseRunId?: string }

type StatusWithAssets = Pick<WorkflowFourStatus, 'steps' | 'chain'> & { assets?: AssetsStatus | null; gates?: WorkflowFourStatus['gates'] };

export const ASSET_TABS: Array<{ key: AssetTabKey; label: string; empty: string }> = [
  // B3-4: con vật nằm trong tab «Nhân vật» (bỏ tab «Con vật»); `assets.tabs.animals` vẫn do BE gửi để tương thích.
  { key: 'characters', label: 'Nhân vật', empty: 'Plan này chưa có nhân vật nào cần ảnh.' },
  { key: 'crowds', label: 'Đám đông', empty: 'Plan này không có đám đông nào cần ảnh nhóm.' },
  { key: 'props', label: 'Đạo cụ', empty: 'Plan này không có đạo cụ nào cần ảnh.' },
  { key: 'locations', label: 'Bối cảnh', empty: 'Plan này chưa có bối cảnh nào cần ảnh.' },
];


// ───────────────────────── bước ② trên thanh bốn bước ─────────────────────────
/** Bước ② đã mở (cổng 1 duyệt, BE trả `assets`) — nút ② trên thanh bước dùng. */
export const assetsUnlockedOf = (status: StatusWithAssets | null | undefined): boolean => !!status?.assets && status.steps?.ASSETS?.locked === false;

/** Màn mặc định: cổng 1 đã duyệt ⇒ ②; chưa ⇒ ①. Người bấm thanh bước thì dùng lựa chọn của họ (nếu bước đó mở). */
export function activeStepOf(status: StatusWithAssets | null | undefined, picked: 'STEP1' | 'ASSETS' | null): 'STEP1' | 'ASSETS' {
  const unlocked = assetsUnlockedOf(status);
  if (picked === 'STEP1') return 'STEP1';
  if (picked === 'ASSETS') return unlocked ? 'ASSETS' : 'STEP1';
  return unlocked ? 'ASSETS' : 'STEP1';
}

export type StepTone = 'ok' | 'warn' | 'bad' | 'run' | 'idle';
/** Nhãn dưới thẻ «2 Tài sản» ở thanh bước. */
export function assetsBadgeOf(status: StatusWithAssets | null | undefined): { text: string; tone: StepTone } {
  const assets = status?.assets;
  const step = status?.steps?.ASSETS;
  if (!assets || !step || step.locked) return { text: '🔒 Khoá', tone: 'idle' };
  switch (step.status) {
    case 'APPROVED': return { text: 'Đã duyệt', tone: 'ok' };
    case 'RUNNING': return { text: `Đang tạo · còn ${assets.progress.remaining} ảnh`, tone: 'run' };
    case 'STOPPED': return { text: 'Đã dừng', tone: 'warn' };
    case 'ERROR': return { text: assets.counts.failed ? `Lỗi ${assets.counts.failed} ảnh` : 'Cần xử lý', tone: 'bad' };
    case 'NOT_STARTED': return { text: 'Chưa tạo ảnh', tone: 'idle' };
    case 'HAS_RESULT':
      if (assets.counts.kycFailed) return { text: `KYC lỗi · ${assets.counts.kycFailed}`, tone: 'bad' };
      if (assets.counts.failed) return { text: `Lỗi ${assets.counts.failed} ảnh`, tone: 'bad' };
      return { text: assets.gate.canApprove ? 'Chờ bạn duyệt' : 'Cần xử lý', tone: 'warn' };
    default: return { text: 'Chưa tới', tone: 'idle' };
  }
}

/** Chuỗi (một thanh tiến độ cho cả bước ① + ②) đang ở đoạn ②. */
/** Đoạn 7..9 (② và ③ nối tiếp); đoạn 10/11 là của ④ nên ② không báo «đang chạy» / «đã dừng» (C1 NOTE-1). */
export const chainInAssetsOf = (status: StatusWithAssets | null | undefined): boolean => { const seg = status?.chain?.segment ?? 0; return seg >= 7 && seg <= 9; };
/** Chuỗi đang chạy ở bước nào: đoạn 7 = ② (tạo ảnh), đoạn 8–9 = ③ (sơ đồ + storyboard), còn lại (kể cả ④) = null. Chữ «đang chạy» của thẻ ② phải nói đúng bước này. */
export const runningStepOf = (status: StatusWithAssets | null | undefined): 'ASSETS' | 'BOARD' | null => {
  if (status?.chain?.state !== 'RUNNING') return null;
  const seg = status.chain.segment ?? 0;
  return seg === 7 ? 'ASSETS' : seg === 8 || seg === 9 ? 'BOARD' : null;
};
/** Đang tạo ảnh — mọi nút ghi khoá (BE trả 409 `WORKFLOW_RUN_ACTIVE`). */
export const assetsRunningOf = (status: StatusWithAssets | null | undefined): boolean => status?.chain?.state === 'RUNNING' && chainInAssetsOf(status);

// ───────────────────────── tab ─────────────────────────
export type TabAttention = 'none' | 'running' | 'bad';
export interface AssetTabSummary { key: AssetTabKey; label: string; empty: string; total: number; ready: number; attention: TabAttention }
/** Thẻ của một tab hiển thị; tab «Nhân vật» gồm cả con vật (B3-4). */
export const tabCardsOf = (assets: AssetsStatus, key: AssetTabKey): Array<AssetCard | AssetLocationCard> =>
  (key === 'characters' ? [...(assets.tabs.characters ?? []), ...(assets.tabs.animals ?? [])] : (assets.tabs[key] ?? [])) as Array<AssetCard | AssetLocationCard>;
const cardsOfTab = (assets: AssetsStatus, key: AssetTabKey): Array<{ status: AssetCardStatus; kyc?: AssetCard['kyc'] }> => tabCardsOf(assets, key) as any;
export function assetTabsOf(assets: AssetsStatus | null | undefined): AssetTabSummary[] {
  return ASSET_TABS.map((tab) => {
    const cards = assets ? cardsOfTab(assets, tab.key) : [];
    const bad = cards.some((c) => c.status === 'FAILED' || c.status === 'BLOCKED' || c.status === 'UNCERTAIN' || (c.kyc && c.kyc.required && (c.kyc.status === 'FAILED' || c.kyc.status === 'UNKNOWN')));
    const running = cards.some((c) => c.status === 'RUNNING' || (c.kyc && c.kyc.status === 'PROCESSING'));
    return { ...tab, total: cards.length, ready: cards.filter((c) => c.status === 'READY').length, attention: bad ? 'bad' : running ? 'running' : 'none' };
  });
}
/** Tab mở đầu: tab đầu tiên có chuyện cần xử lý, rồi tab đầu tiên có thẻ. */
/** C1 Rev 2 — «Về bước 2» từ bước ④: `subjectKey` (`look:` / `prop:` / `location:` / `crowd:`) ⇒ tab + khoá thẻ cần mở. Không thấy thẻ ⇒ null. */
export function assetFocusOf(assets: AssetsStatus | null | undefined, subjectKey: string | null | undefined): { tab: AssetTabKey; key: string } | null {
  if (!assets || !subjectKey) return null;
  for (const t of ASSET_TABS) {
    const cards = tabCardsOf(assets, t.key) as Array<{ key: string; ownerId?: string | null }>;
    const hit = cards.find((c) => c.key === subjectKey || (t.key === 'locations' && !!c.ownerId && `location:${c.ownerId}` === subjectKey));
    if (hit) return { tab: t.key, key: hit.key };
  }
  return null;
}

export function defaultAssetTabOf(assets: AssetsStatus | null | undefined): AssetTabKey {
  const tabs = assetTabsOf(assets);
  return tabs.find((t) => t.attention === 'bad')?.key ?? tabs.find((t) => t.total > 0)?.key ?? 'characters';
}

// ───────────────────────── thẻ ─────────────────────────
export interface AssetChip { text: string; tone: StepTone; title?: string }
const IMAGE_CHIP: Record<AssetCardStatus, AssetChip> = {
  READY: { text: 'Ảnh xong', tone: 'ok' },
  RUNNING: { text: 'Đang tạo', tone: 'run' },
  MISSING: { text: 'Chưa có ảnh', tone: 'idle' },
  STALE: { text: 'Ảnh cũ — cần tạo lại', tone: 'warn' },
  FAILED: { text: 'Lỗi tạo ảnh', tone: 'bad' },
  BLOCKED: { text: 'Chưa tạo được', tone: 'bad' },
  UNCERTAIN: { text: 'Chưa rõ kết quả', tone: 'bad' },
  WAITING_BASE: { text: 'Chờ ảnh gốc', tone: 'idle' },
};
/** Phase S #5 — chip «Hướng mặc định» trên thẻ bối cảnh khi máy chưa gom được hướng riêng. Không có cờ ⇒ null. */
export const directionFallbackChipOf = (card: Pick<AssetLocationCard, 'directionFallback'>): AssetChip | null =>
  card.directionFallback ? { text: 'Hướng mặc định', tone: 'warn' as StepTone, title: 'Máy chưa gom được hướng quay riêng cho bối cảnh này (AI trả lời bị cụt hoặc lỗi), nên dùng hai hướng mặc định: trong và ngoài. Không chặn duyệt.' } : null;
export const imageChipOf = (status: AssetCardStatus): AssetChip => IMAGE_CHIP[status] ?? IMAGE_CHIP.MISSING;

/** Chip KYC. Con vật / engine không cần KYC (`required:false`) ⇒ không chip. */
export function kycChipOf(card: Pick<AssetCard, 'kyc' | 'imageUrl' | 'status'>): AssetChip | null {
  const kyc = card.kyc;
  if (!kyc || !kyc.required || kyc.status === 'NOT_REQUIRED') return null;
  switch (kyc.status) {
    case 'ACTIVE': return { text: 'KYC đã qua', tone: 'ok' };
    case 'PROCESSING': return { text: 'KYC đang kiểm', tone: 'run' };
    case 'FAILED': return { text: 'KYC lỗi', tone: 'bad', title: kyc.message ?? undefined };
    case 'UNKNOWN': return { text: 'KYC chưa rõ', tone: 'bad', title: kyc.message ?? undefined };
    default: return card.imageUrl ? { text: 'Chưa đăng ký KYC', tone: 'warn' } : { text: 'Chờ ảnh để kiểm KYC', tone: 'idle' };
  }
}
/** Chip «ảnh bạn tải lên / sửa» — máy không tạo đè ảnh này. */
export function sourceChipOf(card: { source?: AssetCard['source']; protectedByHuman?: boolean }): AssetChip | null {
  const kind = card.source?.kind;
  if (kind === 'UPLOAD') return { text: 'Ảnh bạn tải lên', tone: 'ok', title: 'Máy không tạo đè ảnh này. Muốn thay thì bấm «Tạo lại» đúng thẻ.' };
  if (kind === 'CUSTOM_PROMPT') return { text: 'Ảnh bạn sửa bằng prompt', tone: 'ok', title: 'Máy không tạo đè ảnh này. Muốn thay thì bấm «Tạo lại» đúng thẻ.' };
  if (card.protectedByHuman) return { text: 'Ảnh của bạn', tone: 'ok', title: 'Máy không tạo đè ảnh này.' };
  return null;
}
const clip = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);
/** Dòng phụ dưới tên. Đám đông phải nói rõ CẦN KYC (BE giữ KYC cho đám đông — Q2), con vật không cần. */
export function cardSubtitleOf(card: AssetCard): string {
  const parts: string[] = [];
  if (card.kind === 'PERSON') {
    // B3 (G4): không tự gán nhãn tuổi khi nguồn không nói — chỉ nói điều biết.
    const known = card.ageOrForm || (card.variantOf ? (card.variantOf.kind === 'AGE' ? 'biến thể tuổi' : 'biến thể hình thái') : null);
    if (known) parts.push(known);
    if (card.variantOf) parts.push(`của ${card.variantOf.name}`);
    if (card.lookKey && card.lookKey !== 'default') parts.push(`bộ «${card.lookKey}»`);
  } else if (card.kind === 'ANIMAL') {
    parts.push(card.species || 'con vật');
    if (card.ageOrForm) parts.push(card.ageOrForm);
  } else if (card.kind === 'CROWD') {
    parts.push(card.ageOrForm || 'nhóm');
    parts.push(card.kyc?.required === false ? 'không cần KYC (engine hiện tại)' : 'cần KYC');
  } else {
    parts.push('đạo cụ');
  }
  if (card.sourceOnly) parts.push('chỉ làm ảnh gốc');
  return parts.join(' · ');
}
/** Câu giải thích khi thẻ không bình thường. */
export function cardNoteOf(card: Pick<AssetCard, 'status' | 'blocker' | 'error' | 'kyc' | 'sourceOnly'> & { kind?: AssetCard['kind'] }): { tone: 'bad' | 'warn' | 'idle'; text: string } | null {
  if (card.status === 'FAILED') return { tone: 'bad', text: card.error?.message?.trim() || 'Tạo ảnh bị lỗi. Bấm «Tạo lại ảnh» để thử lại.' };
  if (card.status === 'UNCERTAIN') return { tone: 'bad', text: 'Máy không chắc lượt tạo ảnh này đã có kết quả hay chưa nên KHÔNG tự gửi lại. Xử lý ở thanh phía dưới («Bỏ lượt chưa rõ») rồi tạo lại.' };
  if (card.status === 'BLOCKED' && card.blocker?.code === 'BLOCKED_WORLD') return { tone: 'bad', text: `${card.blocker.message || 'Chưa có bối cảnh & thời đại hợp lệ.'} Bấm «Sửa» — ngăn Sửa có ô đặt bối cảnh & thời đại của plan.` };
  if (card.status === 'BLOCKED' && card.kind === 'ANIMAL' && card.blocker?.code === 'BLOCKED_INPUT') return { tone: 'bad', text: 'Thiếu loài / mô tả con vật để vẽ ảnh — bấm «Sửa» để điền.' };
  if (card.status === 'BLOCKED') return { tone: 'bad', text: card.blocker?.message || 'Chưa đủ dữ liệu để tạo ảnh — bấm «Sửa» để bổ sung.' };
  if (card.status === 'WAITING_BASE') return { tone: 'idle', text: card.blocker?.message || 'Chờ ảnh bộ chính / nhân vật gốc xong rồi mới tạo.' };
  if (card.kyc?.required && (card.kyc.status === 'FAILED' || card.kyc.status === 'UNKNOWN')) return { tone: 'bad', text: card.kyc.message?.trim() || (card.kyc.status === 'FAILED' ? 'KYC từ chối ảnh này — đổi ảnh rồi kiểm lại.' : 'Chưa rõ kết quả KYC — kiểm lại.') };
  return null;
}

export interface AssetCardActions {
  /** thẻ đã có hàng thật trong Bank (thẻ `missing:` chưa có thì chưa sửa/tạo lại được — bấm «Tạo ảnh còn thiếu») */
  real: boolean;
  edit: boolean;
  regen: boolean;
  regenLabel: string;
  regenPrimary: boolean;
  upload: boolean;
  /** `register` = gọi đăng ký KYC · `open` = mở ngăn Sửa để đối soát dòng treo · null = không có nút */
  kyc: 'register' | 'open' | null;
  disabledReason: string | null;
}
const CARD_DONE: ReadonlySet<string> = new Set(['READY', 'STALE', 'FAILED', 'BLOCKED', 'UNCERTAIN']);
/** Câu khoá của một thẻ khi chuỗi đang chạy — theo bước đang chạy và theo thẻ (ảnh đã xong không bị nói «đang tạo ảnh»). */
export function lockedReasonOf(card: Pick<AssetCard, 'status'>, runStep: 'ASSETS' | 'BOARD' | null): string {
  if (runStep === 'BOARD') return 'Máy đang vẽ sơ đồ và storyboard (bước ③) — chờ xong hoặc bấm Dừng rồi mới sửa ảnh.';
  if (runStep === 'ASSETS' && CARD_DONE.has(String(card.status))) return 'Ảnh này đã xong — chờ máy tạo xong các ảnh còn lại hoặc bấm Dừng rồi mới sửa.';
  return 'Máy đang tạo ảnh — chờ xong hoặc bấm Dừng.';
}
/**
 * Nút trên thẻ. `readOnly` = chuỗi đang chạy (mọi nút ghi khoá). `runStep` = bước đang chạy: chữ khoá nói đúng bước — ③ chạy ⇒ «đang vẽ sơ đồ / storyboard»; ② chạy ⇒ chỉ thẻ CHƯA có ảnh mới nói «đang tạo ảnh», thẻ đã xong nói ảnh đã xong.
 */
export function cardActionsOf(card: AssetCard, readOnly: boolean, runStep: 'ASSETS' | 'BOARD' | null = null): AssetCardActions {
  const real = !!card.ownerId && !card.key.startsWith('missing:');
  const none: AssetCardActions = { real, edit: false, regen: false, regenLabel: 'Tạo lại', regenPrimary: false, upload: false, kyc: null, disabledReason: null };
  if (!real) return { ...none, disabledReason: 'Chưa có hàng trong Bank — máy sẽ tạo khi chạy.' };
  if (readOnly) return { ...none, disabledReason: lockedReasonOf(card, runStep) };
  const uncertain = card.status === 'UNCERTAIN';
  const waiting = card.status === 'WAITING_BASE' || card.status === 'RUNNING';
  const kycBad = !!card.kyc?.required && !!card.imageUrl && ['FAILED', 'MISSING'].includes(card.kyc.status);
  const kycUnknown = !!card.kyc?.required && card.kyc.status === 'UNKNOWN';
  const failed = card.status === 'FAILED' || (kycBad && card.kyc.status === 'FAILED');
  return {
    real, edit: true,
    regen: !uncertain && !waiting && card.status !== 'BLOCKED',
    regenLabel: failed || card.status === 'STALE' ? 'Tạo lại ảnh' : 'Tạo lại',
    regenPrimary: failed || card.status === 'STALE',
    upload: card.kind !== 'CROWD',   // ảnh đám đông chỉ tạo bằng máy / Custom Prompt (Bank hiện không có đường tải lên)
    kyc: kycUnknown ? 'open' : kycBad ? 'register' : null,
    disabledReason: uncertain ? 'Đang chờ bạn bỏ lượt chưa rõ.' : null,
  };
}

/** Thẻ này sửa ở thành phần Bank nào, id nào (ngăn Sửa lọc đúng thẻ này). `needsWorld` = thẻ bị chặn vì chưa có bối cảnh & thời đại hợp lệ ⇒ ngăn Sửa mở khối đặt thời đại của plan. */
export type DrawerTarget =
  | { kind: 'character'; ids: string[]; title: string; needsWorld?: boolean }
  | { kind: 'prop'; ids: string[]; title: string; needsWorld?: boolean }
  | { kind: 'location'; ids: string[]; title: string; needsWorld?: boolean }
  /** Con vật: KHÔNG dùng thẻ nhân vật (mặt / giới tính / trang phục). `ids[0]` = characterId, `lookId` = bộ `default` mang ảnh. */
  | { kind: 'animal'; ids: string[]; lookId: string; species: string | null; title: string; needsWorld?: boolean };
export const isBlockedWorldCard = (card: Pick<AssetCard, 'blocker'> | Pick<AssetLocationCard, 'blocker'>): boolean => card.blocker?.code === 'BLOCKED_WORLD';
export function drawerTargetOf(tab: AssetTabKey, card: AssetCard | AssetLocationCard): DrawerTarget | null {
  const needsWorld = isBlockedWorldCard(card);
  if (tab === 'locations') return card.ownerId ? { kind: 'location', ids: [card.ownerId], title: card.name, needsWorld } : null;
  const c = card as AssetCard;
  if (tab === 'props') return c.ownerId ? { kind: 'prop', ids: [c.ownerId], title: c.name, needsWorld } : null;
  if (tab === 'animals' || c.kind === 'ANIMAL') return c.characterId && c.ownerId ? { kind: 'animal', ids: [c.characterId], lookId: c.ownerId, species: c.species, title: c.name, needsWorld } : null;
  const id = tab === 'crowds' ? (c.characterId || c.ownerId) : c.characterId;
  return id ? { kind: 'character', ids: [id], title: c.name, needsWorld } : null;
}
/** Cách tạo lại một thẻ bằng route Bank có sẵn. */
export type RegenSpec = { api: 'look' | 'crowd' | 'studio'; id: string; ownerType?: 'PROP' | 'LOCATION' };
export function regenSpecOf(tab: AssetTabKey, card: AssetCard | AssetLocationCard): RegenSpec | null {
  if (!card.ownerId) return null;
  if (tab === 'locations') return { api: 'studio', id: card.ownerId, ownerType: 'LOCATION' };
  if (tab === 'props') return { api: 'studio', id: card.ownerId, ownerType: 'PROP' };
  if (tab === 'crowds') return { api: 'crowd', id: (card as AssetCard).characterId || card.ownerId };
  return { api: 'look', id: card.ownerId };
}
/** Tải ảnh lên bằng route Bank có sẵn (đều gửi `planId` để BE ghi nguồn «ảnh của người»). */
export function uploadSpecOf(tab: AssetTabKey, card: AssetCard | AssetLocationCard): { api: 'look' | 'studio'; id: string; ownerType?: 'PROP' | 'LOCATION' } | null {
  if (!card.ownerId || tab === 'crowds') return null;
  if (tab === 'locations') return { api: 'studio', id: card.ownerId, ownerType: 'LOCATION' };
  if (tab === 'props') return { api: 'studio', id: card.ownerId, ownerType: 'PROP' };
  return { api: 'look', id: card.ownerId };
}

// ───────────────────────── bối cảnh + hướng ─────────────────────────
export function locationViewsLineOf(card: AssetLocationCard): string {
  const expected = card.expectedViewKeys?.length || 0;
  const ready = card.views.filter((v) => v.status === 'READY').length;
  if (!card.views.length && !expected) return 'Chưa có ảnh hướng';
  return `Ảnh hướng ${ready}/${Math.max(expected, card.views.length)}`;
}
export const viewLabelOf = (view: AssetLocationView): string => view.label || view.directionKey;

// ───────────────────────── tiền / ước ─────────────────────────
const IMAGE_KIND_TEXT: Array<[keyof AssetsEstimate['images'], string]> = [
  ['look', 'nhân vật'], ['animal', 'con vật'], ['crowd', 'đám đông'], ['prop', 'đạo cụ'], ['location', 'bối cảnh'], ['view', 'ảnh hướng'],
];
/** «38 ảnh: nhân vật 12 · con vật 1 · … » — chỉ liệt kê loại > 0. */
export function estimateBreakdownOf(estimate: AssetsEstimate | null | undefined): string {
  if (!estimate || !(estimate.images?.total > 0)) return '';
  const parts = IMAGE_KIND_TEXT.filter(([k]) => (estimate.images[k] ?? 0) > 0).map(([k, label]) => `${label} ${estimate.images[k]}`);
  // B1-fix2: ước đã gồm lời gọi chữ gom hướng bối cảnh — nói rõ để số lượt chữ không "lệch" so với số ảnh.
  const calls = estimate.calls?.directions ?? 0;
  return `${estimate.images.total} ảnh: ${parts.join(' · ')}${calls > 0 ? ` (+ ${calls} lần gom hướng bối cảnh bằng chữ)` : ''}${estimate.stagingRefresh ? `; đã gồm dự phòng tự làm tươi dàn dựng ${estimate.stagingRefresh.scenes} cảnh` : ''}`;
}
/** «Máy còn kiểm tuổi bằng AI cho 4 ảnh người» — dòng phụ dưới tổng; vắng / 0 ảnh ⇒ null. Không hiện tiền. */
export function ageCheckLineOf(estimate: AssetsEstimate | null | undefined): string | null {
  const a = estimate?.ageCheck;
  if (!a || !(a.images > 0)) return null;
  return `Máy còn kiểm tuổi bằng AI cho ${a.images} ảnh người (chưa gồm trong số ảnh trên).`;
}
export const ESTIMATE_FOOTNOTE = 'Số ảnh là ước, không phải đo theo plan này; chưa gồm KYC. Máy không tạo lại ảnh đã xong, ảnh bạn tải lên hay sửa tay.';

// ───────────────────────── cổng 2 / thanh dưới ─────────────────────────
const BLOCK_TEXT: Record<string, string> = {
  STEP1_NOT_APPROVED: 'Cổng 1 chưa duyệt (hoặc đã cũ) — duyệt lại bước 1 trước.',
  ASSETS_NOT_RUN: 'Chưa tạo ảnh — bấm «Tạo ảnh».',
  ASSETS_RUNNING: 'Máy đang tạo ảnh — đợi xong.',
  ASSETS_TEXT_NOT_READY: 'Phần chữ của tài sản (bước 1) chưa xong — chạy lại bước 1.',
  ASSETS_MISSING_IMAGES: 'Còn thẻ chưa có ảnh hợp lệ.',
  ASSETS_FAILED: 'Còn ảnh lỗi — tạo lại hoặc xử lý trước khi duyệt.',
  ASSETS_VIEWS_MISSING: 'Còn bối cảnh chưa đủ ảnh hướng.',
  KYC_BLOCKING: 'Còn ảnh chưa qua KYC (lỗi / chưa rõ / đang kiểm).',
  UNCERTAIN_OPEN: 'Có lượt chưa rõ kết quả — bỏ lượt chưa rõ trước.',
};
/** Lý do chặn duyệt bằng chữ người đọc: ưu tiên câu BE, rơi về bảng mã. */
export function blockReasonTextOf(why: AssetsBlockReason | null | undefined): string | null {
  if (!why) return null;
  return (why.message && why.message.trim()) || BLOCK_TEXT[why.code] || 'Chưa duyệt được bước 2.';
}
/** Tên các thẻ bị chặn (từ `keys[]` của lý do) — để thanh dưới nói «Orven Senn · Caelan 8 tuổi cần xử lý». */
export function blockedNamesOf(assets: AssetsStatus, why: AssetsBlockReason | null | undefined): string[] {
  if (!why?.keys?.length) return [];
  const keys = new Set(why.keys);
  const named: string[] = [];
  const all: Array<{ key: string; name: string; ageOrForm?: string | null }> = [...assets.tabs.characters, ...assets.tabs.animals, ...assets.tabs.crowds, ...assets.tabs.props, ...assets.tabs.locations];
  for (const c of all) if (keys.has(c.key)) named.push(c.ageOrForm ? `${c.name} ${c.ageOrForm}` : c.name);
  return named;
}

export type FooterKind = 'approve' | 'run' | 'resume' | 'goto-board' | 'none';
export interface AssetsFooter {
  title: string;
  note: string;
  /** dòng lý do chặn (màu cam) */
  hint: string | null;
  primary: { kind: FooterKind; label: string; enabled: boolean };
  /** nút phụ — mỗi cái một việc */
  secondary: Array<{ kind: 'stop' | 'run-missing' | 'run-failed' | 'revoke' | 'resume'; label: string; danger?: boolean }>;
}
/** `nextAction` của BE (status): khi `enabled=false` thì «Chạy tiếp» sẽ lặp đúng lỗi cũ ⇒ không được mời bấm. */
export interface AssetsNextAction { label?: string; enabled?: boolean; why?: string | null }
/** Nhãn BE dùng khi phải đối soát lượt chưa rõ rồi mới chạy tiếp (cùng nút «Chạy tiếp», máy tự đối soát trước). */
const RECONCILE_NEXT_LABEL = 'Đối soát và chạy tiếp';
/**
 * Hành động ĐÚNG khi chuỗi dừng / lỗi ở đoạn ② — dùng chung cho banner và chân màn.
 *  · `nextAction.enabled=false` ⇒ KHÔNG có nút chạy tiếp, nói lý do của BE;
 *  · lượt chưa rõ (UNCERTAIN) ⇒ chỉ «Bỏ lượt chưa rõ (đối soát tay)»;
 *  · còn lại ⇒ «Chạy tiếp» (hoặc «Đối soát và chạy tiếp» khi BE đòi).
 */
export function chainActionOf(opts: { failCode?: string | null; blocksResume?: boolean; nextAction?: AssetsNextAction | null; uncertain?: boolean }): { resume: { label: string } | null; abandon: boolean; blockedWhy: string | null } {
  const uncertain = opts.uncertain === true || failCodeOnlyAbandon(opts.failCode);
  if (uncertain) return { resume: null, abandon: true, blockedWhy: null };
  const abandon = failCodeOffersAbandon(opts.failCode);   // BOTH (vd JOB_RUNNING): vẫn «Chạy tiếp» (máy tự đối soát) + lối «Bỏ lượt chưa rõ»
  if (opts.blocksResume) return { resume: null, abandon: false, blockedWhy: null };
  if (opts.nextAction && opts.nextAction.enabled === false) return { resume: null, abandon, blockedWhy: opts.nextAction.why?.trim() || opts.nextAction.label?.trim() || 'Chạy tiếp sẽ lặp lại đúng lỗi này — cần xử lý trước.' };
  return { resume: { label: opts.nextAction?.label === RECONCILE_NEXT_LABEL ? RECONCILE_NEXT_LABEL : 'Chạy tiếp' }, abandon, blockedWhy: null };
}
/** Thanh hành động ở chân màn ② — một việc chính mỗi trạng thái. `opts.nextAction` = `status.nextAction` của BE. */
/** `opts.boardOpen` (B2) = bước ③ đã mở ⇒ cổng 2 đã duyệt có nút «Sang bước 3». */
export function assetsFooterOf(assets: AssetsStatus, opts: { busy?: boolean; nextAction?: AssetsNextAction | null; failCode?: string | null; boardOpen?: boolean } = {}): AssetsFooter {
  const why = assets.gate.canApproveWhy;
  const reason = blockReasonTextOf(why);
  const names = blockedNamesOf(assets, why);
  const approveLabel = 'Duyệt tài sản';
  const est = assets.estimate;
  const missingN = est?.images?.total ?? 0;
  const failedKeys = assets.lastRun?.failedKeys ?? [];
  const base = { primary: { kind: 'approve' as FooterKind, label: approveLabel, enabled: false }, secondary: [] as AssetsFooter['secondary'] };
  // 261030 S7b fix1 (N1): BE tắt `nextAction` (việc nền đang chạy — vd «Đang làm tươi dàn dựng…» — hoặc lỗi cần xử lý) ⇒ nút duyệt cũng TẮT, vì bấm sẽ ra 409 WORKFLOW_RUN_ACTIVE (smoke dev 03/11: «Duyệt cổng 2» bật sớm ~11 s). Lý do của BE hiện ở `hint`.
  const nextBlocked = opts.nextAction?.enabled === false;
  const nextBlockedHint = nextBlocked ? (opts.nextAction?.why?.trim() || opts.nextAction?.label?.trim() || 'Đang có việc khác chạy — chờ xong rồi duyệt') : null;
  switch (assets.mode) {
    case 'RUNNING':
      return { title: `Máy đang tạo ảnh (${assets.progress.remaining} ảnh còn lại)`, note: 'Ảnh làm song song. Không có AI chấm ảnh; bạn xem rồi quyết. Bạn có thể rời trang, máy vẫn chạy.', hint: 'Đợi ảnh tạo xong.',
        primary: { ...base.primary }, secondary: [{ kind: 'stop', label: 'Dừng', danger: true }] };
    case 'STOPPED': {
      const act = chainActionOf({ nextAction: opts.nextAction, failCode: opts.failCode, uncertain: assets.counts.uncertain > 0 });
      return { title: `Đã dừng — còn ${assets.progress.remaining} ảnh chưa tạo`, note: 'Ảnh đã xong được giữ lại; «Chạy tiếp» chỉ tạo phần còn thiếu. Nếu giữa lúc dừng bạn sửa Bank làm phải tạo thêm ảnh, máy sẽ hỏi xác nhận số ảnh và tiền.', hint: act.blockedWhy,
        primary: act.resume ? { kind: 'resume', label: act.resume.label, enabled: !opts.busy } : { kind: 'none', label: act.abandon ? 'Chờ bỏ lượt chưa rõ' : 'Cần xử lý trước khi chạy tiếp', enabled: false }, secondary: [] };
    }
    case 'ERROR': {
      // Chuỗi FAILED ở đoạn ②: BE từ chối `assets/run` (409 ASSETS_CHAIN_NOT_READY) ⇒ KHÔNG mời «Tạo lại N ảnh lỗi» / «Tạo ảnh còn thiếu» — chỉ đúng việc theo `nextAction`.
      const act = chainActionOf({ nextAction: opts.nextAction, failCode: opts.failCode, uncertain: assets.counts.uncertain > 0 });
      const failedN = assets.counts.failed + assets.counts.blocked + assets.counts.uncertain + assets.counts.kycFailed;
      const secondary: AssetsFooter['secondary'] = [];
      if (assets.gate.canApprove && act.resume) secondary.push({ kind: 'resume', label: act.resume.label });
      const primary: AssetsFooter['primary'] = assets.gate.canApprove ? { kind: 'approve', label: approveLabel, enabled: !opts.busy && !nextBlocked }
        : act.resume ? { kind: 'resume', label: act.resume.label, enabled: !opts.busy }
        : { kind: 'none', label: act.abandon ? 'Chờ bỏ lượt chưa rõ' : 'Cần xử lý trước khi chạy tiếp', enabled: false };
      return { title: failedN ? `Bước 2 dừng vì lỗi — còn ${failedN} ảnh chưa dùng được` : 'Bước 2 dừng vì lỗi', note: act.abandon ? 'Có lượt chưa rõ kết quả — bỏ lượt chưa rõ (đối soát tay) rồi mới chạy tiếp.' : 'Ảnh đã xong được giữ lại; «Chạy tiếp» chỉ tạo phần còn thiếu, không làm lại ảnh đã xong.',
        hint: act.blockedWhy ?? (assets.gate.canApprove ? nextBlockedHint : blockReasonTextOf(why)), primary, secondary };
    }
    case 'READY_TO_START':
      return { title: 'Chưa tạo ảnh cho bước 2', note: estimateBreakdownOf(est) || 'Máy tạo ảnh nhân vật, con vật, đám đông, đạo cụ và bối cảnh.', hint: nextBlockedHint,   // S10 (F13): BE tắt nextAction (việc nền) ⇒ nút TẮT kèm lý do, không bật rồi mới 409
        primary: { kind: 'run', label: missingN ? `Tạo ${missingN} ảnh` : 'Tạo ảnh', enabled: !opts.busy && !nextBlocked }, secondary: [] };
    case 'HAS_RESULT': {
      const secondary: AssetsFooter['secondary'] = [];
      if (failedKeys.length) secondary.push({ kind: 'run-failed', label: `Tạo lại ${failedKeys.length} ảnh lỗi` });
      else if (missingN > 0 && why?.code !== 'KYC_BLOCKING') secondary.push({ kind: 'run-missing', label: `Tạo ${missingN} ảnh còn thiếu` });
      const failed = assets.counts.failed + assets.counts.blocked + assets.counts.uncertain + assets.counts.kycFailed;
      const title = assets.gate.canApprove ? 'Sẵn sàng duyệt bước 2'
          : failed ? `Còn ${failed} ảnh chưa dùng được` : why?.code === 'ASSETS_NOT_RUN' ? 'Chưa tạo ảnh cho bước 2' : 'Chưa duyệt được bước 2';
      const note = assets.gate.canApprove
        ? 'Xem lướt các ảnh, thấy ảnh nào xấu thì «Tạo lại». Duyệt để chốt tài sản; sau đó máy vẽ sơ đồ và storyboard ở bước 3.'
        : names.length ? `${names.join(' · ')} cần xử lý.` : (reason ?? '');
      return { title, note, hint: assets.gate.canApprove ? nextBlockedHint : (reason ?? 'Chưa duyệt được lúc này — tải lại trạng thái; nếu vẫn vậy, báo người phụ trách.'), primary: { kind: 'approve', label: approveLabel, enabled: assets.gate.canApprove && !opts.busy && !nextBlocked }, secondary };
    }
    case 'APPROVED':
      if (opts.boardOpen) return { title: 'Bước 2 đã duyệt', note: 'Các bước sau dựa trên bản ảnh này. Sửa ảnh thì hộp cảnh báo nói rõ phần nào thành cũ.', hint: null,
        primary: { kind: 'goto-board', label: 'Sang bước 3 — Sơ đồ + Storyboard', enabled: !opts.busy }, secondary: [{ kind: 'revoke', label: 'Bỏ duyệt bước 2' }] };
      return { title: 'Bước 2 đã duyệt', note: 'Các bước sau sẽ dựa trên bản ảnh này. Sửa ảnh thì hộp cảnh báo nói rõ phần nào thành cũ.', hint: 'Bước ③ (Sơ đồ + Storyboard) chưa mở — cần cổng 2 còn hiệu lực; tải lại nếu vừa duyệt xong.',
        primary: { kind: 'none', label: 'Sang bước 3 — Sơ đồ + Storyboard', enabled: false }, secondary: [{ kind: 'revoke', label: 'Bỏ duyệt bước 2' }] };
    default:
      return { title: 'Bước 2 chưa mở', note: 'Duyệt cổng 1 trước.', hint: assets.mode === 'LOCKED' ? null : 'Giao diện chưa nhận ra trạng thái bước này — tải lại trang; nếu vẫn vậy, báo người phụ trách.', primary: { ...base.primary }, secondary: [] };   // S10 (F17)
  }
}

/** Banner đỏ đầu màn khi có thẻ KYC lỗi / chưa rõ. */
export function kycBannerOf(assets: AssetsStatus): { message: string; description: string } | null {
  const n = assets.counts.kycFailed;
  if (!n) return null;
  return { message: `${n} ảnh chưa qua KYC`, description: 'KYC kiểm tra gương mặt thật. Chưa qua KYC thì video có người đó sẽ không tạo được. Mở thẻ có chip «KYC lỗi» để tạo lại ảnh hoặc kiểm lại.' };
}

// ───────────────────────── hộp xác nhận ─────────────────────────
/** Hộp «Duyệt cổng 1 và tạo ảnh» (Q5): dòng ước số ảnh + tiền. null = BE chưa có ước ⇒ duyệt thường. */
export function gate1ApproveEstimateOf(status: Pick<WorkflowFourStatus, 'gates'> | null | undefined): { estimate: AssetsEstimate; breakdown: string; startable: boolean } | null {
  const estimate = status?.gates?.STEP1?.assetsEstimate ?? null;
  if (!estimate) return null;
  return { estimate, breakdown: estimateBreakdownOf(estimate), startable: !!estimate.hash };
}
export function newAssetsRunBody(args: { estimate: AssetsEstimate; requestId: string; scope?: 'missing' | 'failed'; baseRunId?: string | null }): AssetsRunBody {
  return { requestId: args.requestId, confirm: true, estimateHash: args.estimate.hash, ...(args.scope ? { scope: args.scope } : {}), ...(args.scope === 'failed' && args.baseRunId ? { baseRunId: args.baseRunId } : {}) };
}

/** Dòng ảnh hưởng của «Tạo lại / Sửa» từ API `assets/impact`. */
/** Rev 4: ảnh ĐẠO CỤ và ĐÁM ĐÔNG không là tham chiếu của sơ đồ / tấm storyboard ⇒ đổi chúng không làm tấm cũ (BE trả mảng rỗng). */
export const boardAffectedByCard = (card: object): boolean => { const kind = (card as { kind?: string }).kind; return kind !== 'PROP' && kind !== 'CROWD'; };
/** Cú bấm nào cũng phải có phản hồi ngay: hộp mở TRƯỚC, dữ liệu phụ (ảnh hưởng, ước) điền khi về. Chờ quá `ms` ⇒ trả null (giữ nội dung mặc định). Lỗi ⇒ null, không ném. */
export const IMPACT_TIMEOUT_MS = 10000;
export function resolveWithin<T>(work: () => Promise<T>, ms: number = IMPACT_TIMEOUT_MS): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    let p: Promise<T>;
    try { p = work(); } catch { clearTimeout(timer); resolve(null); return; }
    p.then((v) => { clearTimeout(timer); resolve(v); }, () => { clearTimeout(timer); resolve(null); });
  });
}
/** Dòng mặc định của hộp cảnh báo khi chưa (hoặc không) biết ảnh hưởng: cổng 2 sẽ thành cũ (+ sơ đồ / storyboard nếu loại thẻ này là tham chiếu của chúng). */
export const IMPACT_LOADING_TEXT = 'Đang xem video nào bị ảnh hưởng…';
export function impactLinesOf(impact: AssetsImpact | null | undefined, gateApproved: boolean, opts: { boardAffected?: boolean } = {}): string[] {
  const lines: string[] = [];
  const subjects = impact?.subjects ?? [];
  const events = new Map<string, { title: string | null; hasStoryboard: boolean; hasMap: boolean; hasClip: boolean }>();
  for (const s of subjects) for (const e of s.events ?? []) events.set(e.eventId, { title: e.title, hasStoryboard: e.hasStoryboard, hasMap: e.hasMap, hasClip: e.hasClip });
  const withBoard = Array.from(events.values()).filter((e) => e.hasStoryboard).length;
  const withMap = Array.from(events.values()).filter((e) => e.hasMap).length;
  const withClip = Array.from(events.values()).filter((e) => e.hasClip).length;
  if (events.size) lines.push(`Ảnh này được dùng trong ${events.size} video.`);
  const boardAffected = opts.boardAffected !== false;
  if (boardAffected && withMap) lines.push(`${withMap} video đã có sơ đồ — sơ đồ đó thành cũ, phải vẽ lại.`);
  if (boardAffected && withBoard) lines.push(`${withBoard} video đã có storyboard — tấm dùng ảnh này thành cũ, phải vẽ lại.`);
  if (withClip) lines.push(`${withClip} video đã có clip — clip không tự đổi; tạo lại clip sẽ tốn thêm tiền.`);
  if (gateApproved || subjects.some((s) => s.approvalImpact?.assetsGate === 'WILL_OUTDATE')) lines.push('Cổng bước 2 vẫn giữ «đã duyệt»; sửa xong có nút «Làm lại từ bước ②» nếu bạn muốn các bước sau theo ảnh mới.');
  return lines;
}
/** Hộp cảnh báo «Sửa ở bước 2 sẽ làm cũ các bước sau» khi cổng 2 đã duyệt. */
export const UPSTREAM_EDIT_TITLE = 'Sửa ở bước 2 sẽ làm cũ các bước sau';
export const upstreamEditLinesOf = (impact: AssetsImpact | null | undefined): string[] => impactLinesOf(impact, true);

// ───────────────────────── lỗi ─────────────────────────
const ASSETS_ERROR_TEXT: Record<string, string> = {
  STEP1_NOT_APPROVED: 'Cổng 1 chưa duyệt hoặc đã cũ — duyệt lại bước 1 trước.',
  ASSETS_CHAIN_NOT_READY: 'Máy chưa ở đúng chỗ để tạo ảnh (chuỗi đang dừng hoặc lỗi) — bấm «Chạy tiếp».',
  ASSETS_USE_RUN_ROUTE: 'Còn ảnh phải tạo thêm — xác nhận số ảnh và tiền ở hộp «Tạo ảnh».',
  ASSETS_TEXT_NOT_READY: 'Phần chữ của tài sản (bước 1) chưa xong — hãy chạy lại bước 1 rồi duyệt lại cổng 1.',
  ASSETS_ESTIMATE_CHANGED: 'Số ảnh cần tạo vừa đổi so với lúc bạn xem — đã tải lại, hãy xem con số mới rồi xác nhận lại.',
  WORKFLOW_RUN_ACTIVE: 'Máy đang chạy — đợi xong hoặc bấm Dừng rồi thử lại.',
  JOB_RUNNING: 'Lượt trước còn mục chưa rõ kết quả hoặc máy đang chạy — nếu máy đã dừng hẳn, bấm «Bỏ lượt chưa rõ (đối soát tay)» rồi chạy tiếp.',
  STEP1_NEEDS_RECONCILE: 'Máy đã tự đối soát nhưng vẫn không chắc một lượt AI đã nhận hay chưa — dùng «Bỏ lượt chưa rõ (đối soát tay)» nếu bạn chắc muốn bỏ.',
  STALE_BASE_RUN: 'Lượt tạo ảnh đã đổi — tải lại rồi bấm «Tạo lại ảnh lỗi» lần nữa.',
  ASSETS_HASH_MISMATCH: 'Ảnh vừa đổi sau khi bạn mở màn này — đã tải lại, hãy xem rồi duyệt lại.',
  GATE_ALREADY_DECIDED: 'Có người vừa duyệt/bỏ duyệt cổng này — đã tải lại.',
  NO_ACTIVE_APPROVAL: 'Bước 2 chưa được duyệt nên không có gì để bỏ.',
  FLOW_NOT_SUPPORTED: 'Plan này không dùng khung 4 bước.',
  BAD_BODY: 'Yêu cầu không hợp lệ — tải lại trang rồi thử lại.',
};
export const assetsErrorCodeOf = (err: any): string | null => {
  const d = err?.response?.data;
  const code = d?.code ?? d?.errorCode;
  return typeof code === 'string' ? code : null;
};
/** Câu lỗi tiếng Việt cho route ②. Mã chưa biết ⇒ null (người gọi rơi về `apiErrorText`). Chỉ có ở 409 ASSETS_ESTIMATE_CHANGED: `estimate` mới. */
export function assetsErrorTextOf(err: any): string | null {
  const code = assetsErrorCodeOf(err);
  if (!code) return null;
  if (code === 'KYC_BLOCKING' || code === 'ASSETS_FAILED' || code === 'ASSETS_MISSING_IMAGES' || code === 'ASSETS_VIEWS_MISSING' || code === 'UNCERTAIN_OPEN' || code === 'ASSETS_NOT_READY' || code === 'ASSETS_NOT_RUN' || code === 'ASSETS_RUNNING') {
    const d = err?.response?.data;
    return (typeof d?.message === 'string' && d.message.trim()) || BLOCK_TEXT[code] || null;
  }
  return ASSETS_ERROR_TEXT[code] ?? null;
}
export const estimateFromErrorOf = (err: any): AssetsEstimate | null => {
  const est = err?.response?.data?.estimate;
  return est && typeof est.hash === 'string' ? (est as AssetsEstimate) : null;
};

export const GATE_NEXT_SEGMENT_STALE_TEXT = 'Đã duyệt bước ①. Dữ liệu bước ① đã đổi nên chưa thể tạo ảnh ngay — bấm «Chạy tiếp» để chạy lại phần ① trước.';
/** Trả lời của `STEP1/approve` có `startAssets`: câu thông báo + có cần nút «Tạo ảnh» không. */
export function approveStartOutcomeOf(response: any, startRequested: boolean): { message: string; needsRunButton: boolean; level: 'success' | 'warning' | 'info' } {
  if (!startRequested) return { message: 'Đã duyệt cổng 1', needsRunButton: false, level: 'success' };
  const a = response?.assets;
  // Phase S (202): BE nhận cú bấm rồi mở ② ở nền — KHÔNG có `assets.error` trong thân; lỗi mở (vd ước đổi) đến qua poll `chain.failReason`.
  if (response?.accepted === true || a?.accepted === true) return { message: 'Đã duyệt cổng 1 — máy đang mở bước 2 và tạo ảnh', needsRunButton: false, level: 'success' };
  if (a?.started) return { message: 'Đã duyệt cổng 1 — máy đang tạo ảnh cho bước 2', needsRunButton: false, level: 'success' };
  if (a?.reason === 'NOTHING_TO_DO') return { message: 'Đã duyệt cổng 1 — không có ảnh nào cần tạo thêm', needsRunButton: false, level: 'info' };
  // S3a: cổng 1 đã ghi xong nhưng chuỗi đang FAILED/STOPPED ở đoạn ② và dữ liệu ① đã cũ ⇒ BE không tự mở (tránh mua lại). KHÔNG phải lỗi; đường đi tiếp là nút «Chạy tiếp» sẵn có.
  if (a?.reason === 'GATE_NEXT_SEGMENT_STALE') return { message: GATE_NEXT_SEGMENT_STALE_TEXT, needsRunButton: false, level: 'info' };
  if (a?.error) return { message: `Đã duyệt cổng 1 nhưng chưa mở được bước 2: ${assetsErrorTextOf({ response: { data: a.error } }) ?? a.error.message ?? 'lỗi chưa rõ'}. Bấm «Tạo ảnh» ở bước 2.`, needsRunButton: true, level: 'warning' };
  return { message: 'Đã duyệt cổng 1', needsRunButton: true, level: 'success' };
}

/** `steps.ASSETS` đang RUNNING hoặc chuỗi ② đang chạy ⇒ phải poll. */
export const assetsPollingOf = (status: StatusWithAssets | null | undefined): boolean => status?.steps?.ASSETS?.status === 'RUNNING' || assetsRunningOf(status) || status?.assets?.mode === 'RUNNING' || !!status?.assets?.counts?.running;

/** Số thẻ KYC đang xử lý ở phía AVIS (poll nhẹ để chip tự đổi sang «đã qua»). */
export const kycPendingOf = (assets: AssetsStatus | null | undefined): number => assets?.counts?.kycPending ?? 0;

/** Số thẻ (mọi tab) — để màn trống nói đúng. */
export const assetCardTotalOf = (assets: AssetsStatus | null | undefined): number => (assets ? assets.counts.total : 0);

/** «Không tạo ảnh theo quyết định ở cổng 1» — nhóm theo loại, giữ thứ tự. */
export function skippedLinesOf(skipped: AssetsSkipped[] | null | undefined): Array<{ key: string; text: string }> {
  return (skipped ?? []).map((s, i) => ({ key: `${s.kind}:${s.ownerId ?? s.name}:${i}`, text: `${s.name} — ${clip(s.reason || 'chỉ nhắc tới, không xuất hiện', 200)}` }));
}
